import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CustomCancellationPolicy } from 'library/helpers/customCancellationPolicy';

// Interfaces for the state structure
interface Coordinates {
  lat: string;
  lng: string;
}

interface Location {
  country: string;
  addressLine1: string;
  addressLine2: string;
  pincode: string;
  landmark: string;
  district: string;
  city: string;
  cityObject: Record<string, any>;
  state: string;
  coordinates: Coordinates;
}

interface Images {
  imageUrlList: Record<string, string[]>;
  thumbnail: Record<string, string>;
}

interface PropertyDetail {
  title: string;
  description: string;
  bnbUrl: string;
  /**
   * `false` once the "Unsync Airbnb Pricing" tool has stopped price sync for this property.
   * Undefined for listings predating the flag — treated as enabled by consumers.
   */
  airbnbPriceSyncEnabled?: boolean;
  property3dUrl: string;
  /** Subjective: USPs + view from the property (required on title step) */
  uspsAndView: string;
  /** Optional objective flags */
  elderlyFriendly: '' | 'yes' | 'no';
  childFriendly: '' | 'yes' | 'no';
  petFriendly: '' | 'yes' | 'no';
  securityGuard24x7: '' | 'yes' | 'no';
  /** standalone | gated_complex */
  propertyStructureType: '' | 'standalone' | 'gated_complex';
  /** House rules entered by the host, shown to guests before check-in */
  houseRules: string;
  /** Amenities follow-up (pool / parking / flexible check-in), filled on amenities step */
  poolLength: string;
  poolBreadth: string;
  poolDepth: string;
  parkingDescription: string;
  /** Flexible check-in/checkout amenity follow-up */
  flexibleCheckinType:
    | ''
    | 'self'
    | 'assisted'
    | 'front_desk'
    | 'digital';
}

interface PriceRange {
  start: number;
  end: number;
}

interface CleaningFee {
  paymentType: string;
  amount: number;
}

interface Discounts {
  lessThan24Hours: number;
  between24HoursTo72Hours: number;
  equalToOrMoreThan72Hours: number;
  equalToOrMoreThan7Days: number;
}

interface BasePrice {
  lessThan24Hours: PriceRange;
  between24HoursTo72Hours: PriceRange;
  equalToOrMoreThan72Hours: PriceRange;
  equalToOrMoreThan7Days: PriceRange;
}

interface WeekdayWeekend {
  pricePerNight: number;
  numberOfGuests: number;
}

interface Friday {
  isSelected: string;
}

interface CleaningFees {
  lessThan3NightsBooking: CleaningFee;
  moreThan3NightsBooking: CleaningFee;
}

interface MaxGuestAllowed {
  numberOfGuests: number;
}

interface PricingDetails {
  defaultPrice?: number | null;
  weekendDefaultPrice?: number | null;
  weekdayMinNights?: number | null;
  weekendMinNights?: number | null;
  weekendDays?: string | null;
  securityDeposit?: number | null;
  maxGuestAllowed: MaxGuestAllowed;
  propertyCalendarPrices?: Record<string, number> | null;
  weekday: WeekdayWeekend;
  weekend: WeekdayWeekend;
  additionalCharges: Record<string, any>[];
  friday: Friday;
  cleaningFees?: CleaningFees;
  discounts: Discounts;
  basePrice: BasePrice;
  discountStore?: any;
}

export interface PartialRefundBand {
  fromDaysBefore: number;
  toDaysBefore: number;
  refundPercentage: number;
}

export interface CancellationPolicy {
  version: 2;
  fullRefundDaysBefore: number;
  partialRefunds: PartialRefundBand[];
  noRefundDaysBefore: number;
}

export const DEFAULT_CANCELLATION_POLICY: CancellationPolicy = {
  version: 2,
  fullRefundDaysBefore: 30,
  partialRefunds: [],
  noRefundDaysBefore: 30,
};

export const MAX_PARTIAL_REFUND_BANDS = 2;

export type CancellationPolicyCode =
  | 'FLEXIBLE' | 'MODERATE' | 'LIMITED' | 'FIRM' | 'NON_REFUNDABLE';

export interface CancellationPolicyTemplate {
  code: string;
  displayName: string;
  description: string;
  sortOrder: number;
}

interface BookingSettings {
  checkInTime: string;
  checkOutTime: string;
  bookingApproval: string;
  cancellationPolicyCode: string | null;
  /** Host-written refund rules, used when cancellationPolicyCode is "CUSTOM". */
  customCancellationPolicy?: CustomCancellationPolicy | null;
  restrictions: string[];
  partialPaymentAllowed: boolean;
  partialPaymentPercentage: number;
}

interface GstDetails {
  isGstRegistered: 'yes' | 'no' | '';
  gstNumber: string;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  accountType: string;
}

interface PlaceSliceState {
  acceptBidResponse: Record<string, any>;
  place: Record<string, any>;
  type: any;
  subType: any;
  discountStore: any;
  location: Location;
  images: Images;
  videoUrl: string | null;
  walkthroughVideoUrl: string | null;
  allowLiveWalkthrough: boolean;
  offerings: any;
  offeringsMap: Record<string, any>;
  detail: PropertyDetail;
  amenities: string[];
  amenitiesObject: any[];
  propertyPhotos: any[];
  storedImages: any[];
  deleteImagesUrl: string[];
  thumbnailUrl: string | number | null;
  pricingDetails: PricingDetails;
  additionalChargesMap: Record<string, any>;
  bookingSettings: BookingSettings;
  gstDetails: GstDetails;
  propertyId: number | string;
  onboardingStage: number | null;
  priceId: string;
  discountId: string;
  businessType: 'B2B' | 'B2C';
}

const initialState: PlaceSliceState = {
  acceptBidResponse: {},
  place: {},
  type: null,
  subType: null,
  discountStore: null,
  location: {
    country: 'India-IN',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    landmark: '',
    district: '',
    city: '',
    cityObject: {},
    state: '',
    coordinates: {
      lat: '',
      lng: '',
    },
  },
  images: {
    imageUrlList: {},
    thumbnail: {},
  },
  videoUrl: null,
  walkthroughVideoUrl: null,
  allowLiveWalkthrough: true,
  offerings: null,
  offeringsMap: {},
  detail: {
    title: '',
    description: '',
    bnbUrl: '',
    property3dUrl: '',
    uspsAndView: '',
    elderlyFriendly: '',
    childFriendly: '',
    petFriendly: '',
    securityGuard24x7: '',
    propertyStructureType: '',
    poolLength: '',
    poolBreadth: '',
    poolDepth: '',
    parkingDescription: '',
    flexibleCheckinType: '',
    houseRules: '',
  },
  amenities: [],
  amenitiesObject: [],
  propertyPhotos: [],
  storedImages: [],
  deleteImagesUrl: [],
  thumbnailUrl: null,
  pricingDetails: {
    defaultPrice: null,
    maxGuestAllowed: {
      numberOfGuests: 0,
    },
    propertyCalendarPrices: null,
    weekday: {
      pricePerNight: 0,
      numberOfGuests: 0,
    },
    weekend: {
      pricePerNight: 0,
      numberOfGuests: 0,
    },
    additionalCharges: [{}, {}, {}],
    friday: {
      isSelected: '',
    },
    cleaningFees: {
      lessThan3NightsBooking: {
        paymentType: 'one_time',
        amount: 0,
      },
      moreThan3NightsBooking: {
        paymentType: 'one_time',
        amount: 0,
      },
    },
    discounts: {
      lessThan24Hours: 0,
      between24HoursTo72Hours: 0,
      equalToOrMoreThan72Hours: 0,
      equalToOrMoreThan7Days: 0,
    },
    basePrice: {
      lessThan24Hours: {
        start: 70,
        end: 100,
      },
      between24HoursTo72Hours: {
        start: 70,
        end: 100,
      },
      equalToOrMoreThan72Hours: {
        start: 70,
        end: 100,
      },
      equalToOrMoreThan7Days: {
        start: 70,
        end: 100,
      },
    },
  },
  additionalChargesMap: {},
  bookingSettings: {
    checkInTime: '',
    checkOutTime: '',
    bookingApproval: '',
    cancellationPolicyCode: null,
    restrictions: [],
    partialPaymentAllowed: true,
    partialPaymentPercentage: 50,
  },
  gstDetails: {
    isGstRegistered: '',
    gstNumber: '',
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: '',
  },
  propertyId: 0,
  onboardingStage: null,
  priceId: '',
  discountId: '',
  businessType: 'B2C',
};
export const placeSlice = createSlice({
  name: 'placeDetails',
  initialState,
  reducers: {
    addPlace: (state, action: PayloadAction<any>) => {
      state.place = action.payload;
    },

    addType: (state, action: PayloadAction<any>) => {
      state.type = action.payload;
    },

    addSubType: (state, action: PayloadAction<any>) => {
      state.subType = action.payload;
    },

    addLocation: (state, action: PayloadAction<Location>) => {
      state.location = action.payload;
    },
    addOfferings: (state, action: PayloadAction<any>) => {
      state.offerings = action.payload;
    },

    addOfferingsMap: (state, action: PayloadAction<Record<string, any>>) => {
      state.offeringsMap = action.payload;
    },

    addDetails: (state, action: PayloadAction<PropertyDetail>) => {
      state.detail = action.payload;
    },

    patchDetail: (state, action: PayloadAction<Partial<PropertyDetail>>) => {
      state.detail = { ...state.detail, ...action.payload };
    },

    addAmenities: (state, action: PayloadAction<string[]>) => {
      state.amenities = action.payload;
    },

    addAmenitiesObject: (state, action: PayloadAction<any[]>) => {
      state.amenitiesObject = action.payload;
    },

    addPricingDetails: (state, action: PayloadAction<PricingDetails>) => {
      // Preserve the existing discountStore when updating pricing details
      const currentDiscountStore = state.discountStore;
      
      state.pricingDetails = action.payload;
      // Only update discountStore if the new payload has one, otherwise preserve existing
      if (action.payload.discountStore) {
        
        state.discountStore = action.payload.discountStore;
      } else if (currentDiscountStore) {
        // Preserve existing discountStore if new payload doesn't have one
        
        state.discountStore = currentDiscountStore;
      } else {
        
        state.discountStore = null;
      }
    },
    addBookingSettings: (state, action: PayloadAction<BookingSettings>) => {
      state.bookingSettings = action.payload;
    },
    addGstDetails: (state, action: PayloadAction<GstDetails>) => {
      state.gstDetails = action.payload;
    },
    setPropertyId: (state, action: PayloadAction<number | string>) => {
      state.propertyId = action.payload;
    },
    setAdditionalChargesMap: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.additionalChargesMap = action.payload;
    },
    addStoredImages: (
      state,
      action: PayloadAction<any[] | Record<string, any>>,
    ) => {
      state.storedImages = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
    },
    addVideoUrl: (state, action: PayloadAction<string | null>) => {
      state.videoUrl = action.payload;
    },
    addWalkthroughVideoUrl: (state, action: PayloadAction<string | null>) => {
      state.walkthroughVideoUrl = action.payload;
    },
    setAllowLiveWalkthrough: (state, action: PayloadAction<boolean>) => {
      state.allowLiveWalkthrough = action.payload;
    },
    /** Loads a saved draft's answers into a fresh onboarding state (see library/helpers/listingDraft). */
    restoreDraft: (state, action: PayloadAction<Record<string, any>>) => {
      const keys = ['place', 'type', 'subType', 'offerings', 'offeringsMap', 'detail', 'location', 'amenities', 'amenitiesObject'];
      const target = state as any;
      const initial = initialState as any;
      Object.keys(initial).forEach((k) => {
        target[k] = initial[k];
      });
      keys.forEach((k) => {
        if (action.payload[k] !== undefined) target[k] = action.payload[k];
      });
    },

    overwritePropertyDetails: (state, action: PayloadAction<any>) => {
      const incomingId = action.payload.propertyId;
      const incomingDetail = action.payload.detail || {};
      const idChanged =
        String(incomingId ?? '') !== String(state.propertyId ?? '');

      // When switching properties, reset detail defaults to avoid leaking fields across listings.
      // For the same property, merge so partial API payloads don't wipe newer client fields.
      state.detail = idChanged
        ? { ...initialState.detail, ...incomingDetail }
        : { ...state.detail, ...incomingDetail };
      state.propertyId = action.payload.propertyId;
      state.amenities = action.payload.amenities;
      state.amenitiesObject = action.payload.amenitiesObject || state.amenitiesObject;
      state.subType = action.payload.subType;
      state.type = action.payload.type;
      state.place = action.payload.place;
      state.location = action.payload.location;
      state.offerings = action.payload?.offerings;
      if (action.payload?.offeringsMap != null) {
        state.offeringsMap = action.payload.offeringsMap;
      }
      if (action.payload?.amenitiesObject != null) {
        state.amenitiesObject = action.payload.amenitiesObject;
      }
      if (typeof action.payload?.onboardingStage === 'number') {
        state.onboardingStage = action.payload.onboardingStage;
      }
    },
    overwritePricing: (state, action: PayloadAction<PricingDetails>) => {
      
      
      state.pricingDetails.additionalCharges = action.payload.additionalCharges;
      state.pricingDetails.discounts = action.payload.discounts;
      state.pricingDetails.weekday = action.payload.weekday;
      state.pricingDetails.weekend = action.payload.weekend;
      state.pricingDetails.propertyCalendarPrices =
        action.payload.propertyCalendarPrices;
      state.pricingDetails.defaultPrice = action.payload?.defaultPrice;
      state.pricingDetails.maxGuestAllowed = action.payload.maxGuestAllowed;
      // Optional fields: only replace the locally persisted value when the server
      // actually returned one, so a partial payload can't blank them.
      const incoming = action.payload as any;
      const stored = state.pricingDetails as any;
      [
        'weekendDefaultPrice',
        'weekdayMinNights',
        'weekendMinNights',
        'weekendDays',
        'securityDeposit',
      ].forEach((k) => {
        if (incoming[k] !== undefined) stored[k] = incoming[k];
      });
      
      // Preserve existing discountStore if incoming payload has null/undefined discountStore
      if (action.payload.discountStore && action.payload.discountStore !== null) {
        state.discountStore = action.payload.discountStore;
        
      } else if (state.discountStore) {
        
      } else {
        state.discountStore = action.payload.discountStore;
        
      }
      
      
    },
    addThumbnailUrl: (state, action: PayloadAction<Record<string, string>>) => {
      let index = state.storedImages.length;
      
      state.storedImages = [...state.storedImages, { other: [action.payload] }];
      state.thumbnailUrl = index;
    },
    updateOnboardingStage: (state, action: PayloadAction<number>) => {
      state.onboardingStage = action.payload;
    },
    updatePriceId: (state, action: PayloadAction<string>) => {
      state.priceId = action.payload;
    },
    updateDiscountId: (state, action: PayloadAction<string>) => {
      state.discountId = action.payload;
    },
    updateDiscountStore: (state, action: PayloadAction<any>) => {
      
      state.discountStore = action.payload;
    },
    setImages: (state, action: PayloadAction<Images>) => {
      state.images.imageUrlList = action.payload.imageUrlList;
      state.images.thumbnail = action.payload.thumbnail;
    },
    addImage: (
      state,
      action: PayloadAction<{
        category: string;
        url?: string;
        imageUrl?: string;
      }>,
    ) => {
      
      state.images.imageUrlList = {
        ...state.images.imageUrlList,
        [action.payload.category]: state.images.imageUrlList[
          action.payload.category
        ]
          ? [
              ...state.images.imageUrlList[action.payload.category],
              action.payload.url || action.payload.imageUrl || '',
            ]
          : [action.payload.url || action.payload.imageUrl || ''],
      };
      
    },
    deleteImage: (
      state,
      action: PayloadAction<{ category: string; url: string }>,
    ) => {
      state.images.imageUrlList = {
        ...state.images.imageUrlList,
        [action.payload.category]: state.images.imageUrlList[
          action.payload.category
        ].filter((url) => url !== action.payload.url),
      };
    },
    setThumbnail: (
      state,
      action: PayloadAction<string | Record<string, string>>,
    ) => {
      
      if (typeof action.payload === 'string') {
        state.images.thumbnail = { default: action.payload };
      } else {
        state.images.thumbnail = action.payload;
      }
    },
    setAcceptBidResponse: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.acceptBidResponse = action.payload;
    },
    setBusinessType: (state, action: PayloadAction<'B2B' | 'B2C'>) => {
      state.businessType = action.payload;
    },
    updateStore: (state, action: PayloadAction<boolean>) => {
      return initialState;
    },
  },
});

export const {
  addPlace,
  addType,
  addSubType,
  addLocation,
  addOfferings,
  addDetails,
  patchDetail,
  addAmenities,
  addAmenitiesObject,
  addPricingDetails,
  addBookingSettings,
  addGstDetails,
  setPropertyId,
  addOfferingsMap,
  setAdditionalChargesMap,
  addStoredImages,
  restoreDraft,
  overwritePropertyDetails,
  overwritePricing,
  addThumbnailUrl,
  updateOnboardingStage,
  updatePriceId,
  updateDiscountId,
  updateDiscountStore,
  updateStore,
  setImages,
  addImage,
  deleteImage,
  setThumbnail,
  setAcceptBidResponse,
  setBusinessType,
  addVideoUrl,
  addWalkthroughVideoUrl,
  setAllowLiveWalkthrough,
} = placeSlice.actions;

export default placeSlice.reducer;
